/**
 * "Speed with the music" — the one vocabulary shared by every effect that can follow a beat.
 *
 * The controller's six lock intervals. The effect's numeric speed parameter keeps its declared unit
 * when unlocked; the lock takes that parameter over when enabled.
 */
export const MUSIC_RATES = ['beat', '2beats', 'bar', '2bars', '4bars', '8bars'] as const;

export type MusicRate = (typeof MUSIC_RATES)[number];

const LABELS: Record<MusicRate, string> = {
  beat: 'Every beat',
  '2beats': 'Every 2 beats',
  bar: 'Every bar',
  '2bars': 'Every 2 bars',
  '4bars': 'Every 4 bars',
  '8bars': 'Every 8 bars',
};

/** Shorthand for the segmented control, which is too narrow for the full sentence. */
const SHORT_LABELS: Record<MusicRate, string> = {
  beat: '1 beat',
  '2beats': '2 beats',
  bar: '1 bar',
  '2bars': '2 bars',
  '4bars': '4 bars',
  '8bars': '8 bars',
};

export function isMusicRate(value: unknown): value is MusicRate {
  return typeof value === 'string' && (MUSIC_RATES as readonly string[]).includes(value);
}

/** The operator-facing sentence. An unknown value from an older controller is shown as-is, never hidden. */
export function musicRateLabel(rate: string | undefined): string {
  return isMusicRate(rate) ? LABELS[rate] : String(rate ?? '');
}

export function musicRateShortLabel(rate: string | undefined): string {
  return isMusicRate(rate) ? SHORT_LABELS[rate] : String(rate ?? '');
}
